import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './Projects.css';

const Projects = () => {
  const [filter, setFilter] = useState('All');

  const projects = [
    {
      id: 1,
      title: "Let's L-Earn and Lead",
      category: 'Web',
      description: 'Comprehensive learning management system with interactive features, payment integration, and secure content delivery.',
      technologies: ['React', 'Node.js', 'MongoDB', 'TypeScript', 'JWT', 'Razorpay'],
      image: '/lms-hero.png',
      liveDemo: 'https://letslearnandlead.com',
      github: null,
      featured: true
    },
    {
      id: 2,
      title: 'Garbit E-commerce Platform',
      category: 'Web',
      description: 'Comprehensive multi-vendor marketplace with modern design, extensive features for vendors and customers, and robust user-friendly interface.',
      technologies: ['React', 'JavaScript', 'CSS3', 'REST API', 'Redux'],
      image: '/garbit-ecommerce.png',
      liveDemo: '#',
      github: 'https://github.com/Krishna199399/Grabit-E-Commerce',
      featured: false
    },
    {
      id: 3,
      title: 'TeachPrep AI',
      category: 'AI',
      description: 'AI-powered platform for educators to create, manage, and enhance teaching materials with intelligent lesson planning and collaborative tools.',
      technologies: ['React', 'Next.js', 'TypeScript', 'Tailwind CSS', 'OpenAI API'],
      image: '/teachprep-ai.png',
      liveDemo: '#',
      github: 'https://github.com/Krishna199399/TeachPrep-AI',
      featured: false
    },
    {
      id: 4,
      title: 'Breast Cancer Detection System',
      category: 'AI',
      description: 'CNN-based medical diagnostic system for classifying breast histology images with role-based authentication.',
      technologies: ['Python', 'TensorFlow', 'Keras', 'Flask', 'NumPy'],
      image: '/breast-cancer-detection.png',
      liveDemo: '#',
      github: 'https://github.com/Krishna199399/CNN-Breast-Cancer-Detection',
      featured: false
    },
    {
      id: 5,
      title: 'Hostel Management System',
      category: 'Web',
      description: 'Complete hostel management solution with room allocation, student records, fee management, and admin dashboard built using MERN stack.',
      technologies: ['React', 'Node.js', 'Express', 'MongoDB'],
      image: '/hostel-management.png',
      liveDemo: '#',
      github: 'https://github.com/Krishna199399/Hostel_management_system',
      featured: false
    }
  ];

  const categories = ['All', 'Web', 'AI'];

  const filteredProjects = filter === 'All' 
    ? projects 
    : projects.filter(p => p.category === filter);

  return (
    <section id="projects" className="projects-premium">
      {/* Ambient Background */}
      <div className="projects-ambient">
        <div className="ambient-gradient-projects"></div>
        <div className="ambient-grain-projects"></div>
      </div>

      <div className="projects-container-premium">
        {/* Section Title */}
        <motion.h2 
          className="projects-title-premium"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          PROJECTS
        </motion.h2>

        {/* Filter Pills */}
        <motion.div 
          className="filter-pills"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
        >
          {categories.map((cat) => (
            <motion.button
              key={cat}
              className={`filter-pill ${filter === cat ? 'active' : ''}`}
              onClick={() => setFilter(cat)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {cat}
            </motion.button>
          ))}
        </motion.div>

        {/* Projects Grid */}
        <motion.div 
          className="projects-grid-premium"
          layout
        >
          <AnimatePresence mode="wait">
            {filteredProjects.map((project, index) => (
              <motion.div
                key={project.id}
                className={`project-card-premium ${project.featured ? 'featured' : ''}`}
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                transition={{
                  duration: 0.6,
                  delay: index * 0.1,
                  ease: [0.22, 1, 0.36, 1]
                }}
                whileHover={{
                  y: -8,
                  transition: { duration: 0.3 }
                }}
                layout
              >
                {project.featured && (
                  <div className="featured-badge-premium">FEATURED</div>
                )}

                {/* Project Image */}
                <div className="project-image-premium">
                  {project.image ? (
                    <img 
                      src={project.image} 
                      alt={project.title}
                      className="project-image-actual"
                    />
                  ) : (
                    <div className="image-placeholder-premium">
                      {project.category}
                    </div>
                  )}
                  <motion.div 
                    className="image-overlay"
                    initial={{ opacity: 0 }}
                    whileHover={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                  />
                </div>

                {/* Project Content */}
                <div className="project-content-premium">
                  <div className="project-category-label">{project.category}</div>
                  
                  <h3 className="project-title-card">{project.title}</h3>
                  
                  <p className="project-description-premium">{project.description}</p>

                  {/* Tech Stack Pills */}
                  <div className="tech-stack-pills">
                    {project.technologies.map((tech, idx) => (
                      <span key={idx} className="tech-pill">{tech}</span>
                    ))}
                  </div>

                  {/* Action Buttons */}
                  <div className="project-actions">
                    {project.liveDemo && project.liveDemo !== '#' && (
                      <motion.a
                        href={project.liveDemo}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="action-btn primary-action"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        Live Demo
                      </motion.a>
                    )}
                    {project.github && (
                      <motion.a
                        href={project.github}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="action-btn secondary-action"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        GitHub
                      </motion.a>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
};

export default Projects;
